import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { ApiError } from "@/lib/api";

import { useAddItem } from "../cart/hooks";
import { QuoteEstimator } from "../pricing/QuoteEstimator";
import { track } from "../lib/analytics";
import { PhotoGallery } from "./PhotoGallery";
import { PriceTierBlock } from "./PriceTierBlock";
import { SpecsTable } from "./SpecsTable";
import { usePublicType } from "./api";
import type { TypeDetail } from "./types";

// FR-4 — /c/:category_slug/:type_slug. Gallery, title, pricing block,
// specs table, spec sheet PDF link, sticky-on-mobile Reserve CTA, and
// the contract §17 standing-charges disclosure. The route owns the
// type_slug; the category_slug in the URL is for SEO/breadcrumb only,
// so a mismatch with the type's actual category quietly redirects to
// the canonical URL via react-router.
export function TypeDetailPage(): JSX.Element {
  const { type_slug = "", category_slug = "" } = useParams<{
    type_slug: string;
    category_slug: string;
  }>();
  const navigate = useNavigate();
  const { data, isPending, isError, error } = usePublicType(type_slug);

  useEffect(() => {
    if (type_slug) track({ name: "type_viewed", type_slug });
  }, [type_slug]);

  // If the URL's category slug doesn't match the type's real category,
  // rewrite to the canonical path so external links don't get stuck on
  // a stale category prefix.
  useEffect(() => {
    if (data && data.category.slug && data.category.slug !== category_slug) {
      navigate(`/c/${data.category.slug}/${data.slug}`, { replace: true });
    }
  }, [data, category_slug, navigate]);

  if (isPending) return <TypeDetailSkeleton />;
  if (isError) {
    return (
      <Alert variant="destructive">
        We couldn&rsquo;t load this equipment.
        {error instanceof ApiError ? ` (HTTP ${error.status})` : null}
      </Alert>
    );
  }
  if (!data) return <TypeNotFound />;

  return <TypeDetailBody type={data} />;
}

function TypeDetailBody({ type }: { type: TypeDetail }): JSX.Element {
  return (
    <div className="pb-24 md:pb-0">
      <nav aria-label="Breadcrumb" className="mb-4 text-sm text-zinc-600">
        <Link to="/" className="hover:underline">Home</Link>
        <span className="mx-1 text-zinc-400" aria-hidden="true">/</span>
        <Link to={`/c/${type.category.slug}`} className="hover:underline">
          {type.category.name}
        </Link>
        <span className="mx-1 text-zinc-400" aria-hidden="true">/</span>
        <span className="text-brand-black">{type.name}</span>
      </nav>

      <div className="grid gap-6 md:grid-cols-2">
        <PhotoGallery photos={type.photos} typeName={type.name} />

        <div className="space-y-5">
          <header>
            <h1 className="text-2xl font-semibold text-brand-black md:text-3xl">{type.name}</h1>
            {type.summary ? (
              <p className="mt-2 text-sm text-zinc-700">{type.summary}</p>
            ) : null}
          </header>

          <PriceTierBlock type={type} />

          <QuoteEstimator type={type} />

          {type.spec_sheet_url ? (
            <p>
              <a
                href={type.spec_sheet_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-brand-red hover:underline"
              >
                Download spec sheet (PDF) ↗
              </a>
            </p>
          ) : null}

          <div className="hidden md:block">
            <ReserveButton type={type} />
          </div>
        </div>
      </div>

      {type.description ? (
        <section className="mt-8" aria-labelledby="overview-heading">
          <h2 id="overview-heading" className="mb-2 text-lg font-semibold text-brand-black">
            Overview
          </h2>
          <p className="max-w-3xl whitespace-pre-line text-sm text-zinc-700">{type.description}</p>
        </section>
      ) : null}

      {type.specs && Object.keys(type.specs).length > 0 ? (
        <section className="mt-8" aria-labelledby="specs-heading">
          <h2 id="specs-heading" className="mb-2 text-lg font-semibold text-brand-black">
            Specs
          </h2>
          <SpecsTable specs={type.specs} />
        </section>
      ) : null}

      <p className="mt-8 max-w-3xl text-xs text-zinc-500">
        All rentals include a delivery fee, environmental services charge, and applicable taxes.
        See the full breakdown at checkout.
      </p>

      {/* Sticky mobile-only Reserve bar. iOS keyboard avoidance is left
          to the page chrome — the input fields here are server-side
          read-only so the bar doesn't intercept input focus. */}
      <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-zinc-200 bg-brand-white p-3 shadow-[0_-2px_8px_rgba(0,0,0,0.05)] md:hidden">
        <ReserveButton type={type} fullWidth />
      </div>
    </div>
  );
}

// "Reserve this equipment" pushes the type into the customer's cart
// and lands them on /cart. The cart is server-side (anonymous via
// signed cookie until inline signup) — see app/models/cart.rb. We
// add a single line per click; quantity > 1 is achieved by re-adding
// since the rental engine binds each line to its own unit at finalize.
function ReserveButton({
  type,
  fullWidth = false
}: {
  type: TypeDetail;
  fullWidth?: boolean;
}): JSX.Element {
  const navigate = useNavigate();
  const addItem = useAddItem();
  return (
    <Button
      type="button"
      size="lg"
      className={fullWidth ? "w-full" : ""}
      disabled={addItem.isPending}
      onClick={() => {
        track({ name: "reserve_clicked", type_slug: type.slug });
        addItem.mutate(
          {
            type_id: type.id,
            operator_hours: 0,
            damage_waiver: false,
            attachment_type_ids: []
          },
          { onSuccess: () => navigate("/cart") }
        );
      }}
    >
      {addItem.isPending ? "Adding…" : "Reserve this equipment"}
    </Button>
  );
}

function TypeDetailSkeleton(): JSX.Element {
  return (
    <div className="space-y-4" aria-busy="true">
      <Skeleton className="h-4 w-1/2" />
      <div className="grid gap-6 md:grid-cols-2">
        <Skeleton className="aspect-[4/3] w-full" />
        <div className="space-y-3">
          <Skeleton className="h-8 w-2/3" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    </div>
  );
}

function TypeNotFound(): JSX.Element {
  return (
    <div className="rounded-md border border-zinc-200 bg-brand-white p-8 text-center">
      <h1 className="text-xl font-semibold text-brand-black">Equipment not found</h1>
      <p className="mt-2 text-sm text-zinc-600">
        The URL may be stale or this equipment was archived.
      </p>
      <p className="mt-4">
        <Link to="/" className="text-brand-red hover:underline">Browse all categories</Link>
      </p>
    </div>
  );
}
